export const fragmentShader = `
  uniform float u_time;
  uniform vec2 u_res;
  uniform vec3 u_skyColor;
  uniform vec3 u_cloudColor;
  uniform float u_rainIntensity;
  uniform float u_snowIntensity;
  uniform float u_lightningFlash;
  uniform float u_quality;
  
  float hash(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  
  float noise(vec2 x) {
    vec2 i = floor(x);
    vec2 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);
    
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  
  float fbm(vec2 x) {
    float v = 0.0;
    float a = 0.5;
    const mat2 rot = mat2(0.8776, 0.4794, -0.4794, 0.8776);
    
    int maxIter = int(2.0 + u_quality * 3.0);
    
    for (int i = 0; i < 5; i++) {
      if (i >= maxIter) break;
      v += a * noise(x);
      x = rot * x * 2.0;
      a *= 0.5;
    }
    return v;
  }

  float rain(vec2 uv, float t) {
    vec2 st = uv * vec2(30.0, 1.0);
    float id = floor(st.x);
    st.y += t * 20.0 + hash(vec2(id, 0.0)) * 100.0;
    
    float n = noise(st);
    st.y = fract(st.y);
    
    float mask = step(0.95, n) * smoothstep(0.0, 0.1, st.y) * smoothstep(1.0, 0.9, st.y);
    return mask;
  }

  float snow(vec2 uv, float t) {
    float snowAccum = 0.0;
    int maxLayers = int(1.0 + u_quality * 2.0);
    
    for (int layer = 0; layer < 3; layer++) {
      if (layer >= maxLayers) break;
      
      float layerScale = 1.0 + float(layer) * 0.5;
      float layerSpeed = 0.3 + float(layer) * 0.2;
      
      vec2 st = uv * vec2(20.0 / layerScale, 10.0 / layerScale);
      vec2 cellId = floor(st);
      vec2 cellUv = fract(st);
      
      vec2 snowPos = vec2(hash(cellId + 0.1), hash(cellId + 0.2));
      
      float fallY = fract(snowPos.y - t * layerSpeed);
      float driftX = sin(t * 0.5 + snowPos.x * 6.28) * 0.15;
      
      vec2 snowCenter = vec2(snowPos.x + driftX, fallY);
      float dist = length(cellUv - snowCenter);
      
      float snowSize = 0.02 + hash(cellId + 0.3) * 0.03;
      snowSize /= layerScale;
      
      float snowMask = u_quality > 0.75 
        ? smoothstep(snowSize, snowSize * 0.3, dist)
        : step(dist, snowSize * 0.5);
      
      float alpha = hash(cellId + 0.4) * 0.7 + 0.3;
      snowAccum += snowMask * alpha / float(layer + 1);
    }
    
    return snowAccum;
  }

  void main() {
    vec2 uv = gl_FragCoord.xy / u_res.xy;
    vec2 aspectUV = uv * vec2(u_res.x / u_res.y, 1.0);

    float time = u_time * 0.05;
    vec2 flow = vec2(time, time * 0.2);
    vec2 pos = aspectUV * 1.5 + flow;
    float clouds = fbm(pos + fbm(pos * 2.0));
    float cover = smoothstep(0.3, 0.8, clouds);
    
    vec3 bg = mix(u_skyColor, u_cloudColor, cover);

    if (u_rainIntensity > 0.01) {
      float r1 = rain(uv + vec2(0.1), u_time) * 0.5;
      float r2 = u_quality > 0.5 ? rain(uv * 1.5 + vec2(0.2), u_time * 1.2) * 0.3 : 0.0;
      float totalRain = (r1 + r2) * u_rainIntensity * 2.0;
      bg = mix(bg, vec3(0.7, 0.8, 0.9), totalRain);
    }

    if (u_snowIntensity > 0.01) {
      float totalSnow = snow(uv, u_time * 0.5) * u_snowIntensity;
      bg = mix(bg, vec3(0.95, 0.97, 1.0), totalSnow);
    }

    if (u_lightningFlash > 0.01) {
      bg = mix(bg, vec3(0.9, 0.92, 1.0), u_lightningFlash * 0.7);
    }

    float dist = u_quality > 0.75 
      ? distance(uv, vec2(0.5))
      : max(abs(uv.x - 0.5), abs(uv.y - 0.5)) * 1.2;
    float vig = smoothstep(0.4, 1.2, dist);
    bg = mix(bg, vec3(0.0), vig * 0.8);

    gl_FragColor = vec4(bg, 1.0);
  }
`;

export const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
